import { PermissionGroup } from '@ag1/atlas-db';
import { z } from 'zod';

import { deviceTypesSchema } from '@/schemas';

import type { AtlasApiClient } from '@ag1/atlas-api/client';
import type { BackendFormSchema, MinItemArray, SupportedAssetLibraryComponentType } from '@ag1/ui';

export * from './offer';

export type SectionType = 'builtin' | 'custom' | 'static' | 'asset_library';

export interface CustomUrlsRow {
  id: number;
  page_id: number;
  from: string;
  to: string;
}

export enum PageStatus {
  Draft = 'draft',
  Published = 'published',
  Archived = 'archived'
}
export const isPageStatus = (status: string): status is PageStatus =>
  Object.values(PageStatus).includes(status as any);

export enum AssetStatus {
  Approved = 'approved',
  Published = 'published',
  SavedChange = 'saved changes',
  ResotredVersion = 'restored version'
}
export const isAssetStatus = (status: string): status is AssetStatus =>
  Object.values(AssetStatus).includes(status as any);

export const AssetSections = {
  sections: 'sections',
  faqs: 'faqs',
  reviews: 'reviews'
} as const;

export const isAssetSections = (
  section: string
): section is (typeof AssetSections)[keyof typeof AssetSections] =>
  Object.values(AssetSections).includes(section as any);

export enum NotificationTabs {
  All = 'all',
  ActionNeeded = 'actionNeeded'
}

export interface PageRepository {
  id: number;
  name: string;
  title: string;
  sections2: SupabaseSection[];
  pageUrl: string;
  created_by: string;
  status: PageStatus;
  created_at: Date;
  created_by_name: string;
  created_by_user_id: null | string;
  CustomUrls: null | CustomUrlsRow;
  page_editing_user_roles: null | PageEditingUserRole[];
  created_by_user: null | User;
  parent_page_id: number | null;
  is_experiment_variant: boolean;
  frictionless: boolean;
  is_hidden: boolean;
  /**
   * Only be nullable for Atlas pages created before OfferBuilder release.
   */
  offer_id: number | null;
  checkout_flow: PageCheckoutFlow | null;
  totalSessions: number;
  avgCvr: number;
  totalCvrs: number;
  bounceRate: number;
  use_pdp: boolean;
  pdp_url: string | null;
}

// Sync with `SUPPORTED_CHECKOUT_FLOW_TYPES` in Portal.
export const CHECKOUT_FLOW_TYPES = ['bab'] as const;
export type CheckoutFlowType = (typeof CHECKOUT_FLOW_TYPES)[number];
export const isCheckoutFlowType = (value: any): value is CheckoutFlowType =>
  CHECKOUT_FLOW_TYPES.includes(value);

export interface PageCheckoutFlow {
  type: CheckoutFlowType;
}

/**
 * Helper function that allows proper type checking of switch cases.
 *
 * Provide no `default` case and return this after.
 */
export const assertNever = <ProdFallback = never>(
  x: never,
  prodFallback: ProdFallback = undefined as never
): ProdFallback => {
  if (process.env.NODE_ENV === 'production') {
    return prodFallback;
  }
  throw new Error(`Unexpected object: ${x}`);
};

export enum EventItemType {
  UserReviewRequestEvent = 'user_review_request_event'
  // below are the types which we might have in future
  // EditAccessGrantedEvent = 'edit_access_granted_event',
  // OfferStoppedEvent = 'offer_stopped_event'
}

export interface NotificationRepository {
  id: number;
  page_name: string;
  page_id?: number;
  title: string;
  location?: string;
  created_at: Date;
  created_by_name?: string;
  requires_action: boolean;
  avatar_url?: string;
  event_type: string;
}

export const enum NotificationIndicatorState {
  HideNotification = 0,
  ReadNotification = 1,
  NewNotification = 2
}

export const enum ActionPanelContents {
  None = 0,
  Notifications = 1,
  Resources = 2
}

/**
 * Can accept a `PageRepository` or a `PageRepository`-like object. Pass in only
 * `id` to get the default ID-based slug.
 *
 * NOTE: Slugs returned here don't include domain or locale. Full page URL
 * should be `${DYNAMIC_PAGE_URL}${getPageRepositorySlug(page)}`.
 */
export const getPageRepositorySlug = ({
  CustomUrls,
  id
}: {
  CustomUrls?: PageRepository['CustomUrls'];
  id: number;
}) => (CustomUrls && CustomUrls.from ? CustomUrls.from : `/landers/${id}`);

/**
 * Section info stored in Supabase. Used to construct a {@link SectionDetail} by
 * combining this data with our {@link SectionConfigDefaults}.
 *
 * NOTE: Check Portal implementation of SupabaseSection if changing this.
 */
export interface SupabaseSection {
  /**
   * Treated as a `slug` for both built-in and custom sections. Custom sections
   * are a uuid prefixed with `custom_`.
   */
  id: string | number;
  type: SectionType;
  name: SectionName;
}

/**
 * Index of items determines the order in which static sections are rendered.
 */
export const STATIC_SECTION_NAMES = ['Offer Anchor'] as const;

export const STATIC_SECTIONS: Readonly<
  Record<
    StaticSectionName,
    | {
        /** Parent section to pull a section preview image from. */
        previewParent: BuiltInSectionName;
        preview?: never;
      }
    | {
        previewParent?: never;
        /** Image to use for the section preview. */
        preview: string;
      }
  >
> = {
  'Offer Anchor': { previewParent: 'Header' }
} as const;

export const BuiltInSectionNames = [
  'Header',
  'Hero',
  'Offer',
  'FAQ',
  'Benefits',
  'How To Use',
  'Customer Reviews',
  'Testimonials',
  'Reasons to Believe',
  'Research',
  'Replacement Value',
  'Quality',
  'Ingredients',
  'Closing Hook',
  'Recipes',
  'Taste',
  'Dietary List',
  'Video Gallery'
] as const;

export const CustomSectionNames = [
  'Image & Text',
  'List & Image',
  'Image Gallery',
  'Expandable List',
  'offer-builder-bab'
] as const;

export type StaticSectionName = (typeof STATIC_SECTION_NAMES)[number];
export type BuiltInSectionName = (typeof BuiltInSectionNames)[number];
export type CustomSectionName = (typeof CustomSectionNames)[number];
/** A combination of all the section names (builtin, custom) */
export type SectionName =
  | StaticSectionName
  | BuiltInSectionName
  | CustomSectionName
  | SupportedAssetLibraryComponentType;

export interface CustomContent {
  id: number;
  page_id: number;
  version: number;
  section_slug: string;
  content_type: string;
  is_current: boolean;
  content: string;
}

export type CreateCustomContentPayload = Omit<CustomContent, 'id' | 'version' | 'page_id'>;

export type CustomContentOrPayload = CustomContent | CreateCustomContentPayload;

export interface CustomContentTableRow {
  id: number;
  created_at: Date;
  page_id: number | null;
  version: number | null;
  section_slug: string | null;
  content_type: string | null;
  is_current: boolean;
  content: string | null;
  locale: string;
}

// The structure of public.analytics_page_cache supabase table.
export interface AnalyticsPageCache {
  id: number;
  page_id: number;
  updated_at: Date;
  data: AnalyticsPageCacheData[];
}

// The structure of public.analytics_page_cache.data field.
export interface AnalyticsPageCacheData {
  date: string; // YYYY-MM-DD
  numSessions: number;
  avgDurationMin: number;
  numBounces: number;
  numCvrs: number;
}

export interface EditingUserRoleBase {
  id?: number;
  page_id?: number;
  user_id?: string;
  created_at?: Date;
  role?: UserRole;
  users?: User;
}

export interface PageEditingUserRole extends EditingUserRoleBase {
  page_id?: number;
}

export interface OfferEditingUserRole extends EditingUserRoleBase {
  offer_id?: number;
}

export enum UserRole {
  Owner = 'owner',
  Editor = 'editor',
  Viewer = 'viewer'
}

export enum UserType {
  User = 'user',
  Admin = 'admin'
}

export const isValidUserType = (type: string): type is UserType =>
  Object.values(UserType).includes(type as any);

type ExcludeByPrefix<T, Prefix extends string> = T extends `${Prefix}${infer _}` ? never : T;
// Note: Exclude dev-specific permissions such as customize-* from here
export type NonDeveloperPermissionGroup = ExcludeByPrefix<PermissionGroup, 'customize-'>;

export enum ValidPermissionsValues {
  Offer = 'offer',
  Frictionless = 'frictionless',
  PurchaseDashboard = 'purchase_dashboard',
  AssetLibrary = 'asset_library'
}

export const isValidUserPermission = (permission: string): permission is PermissionGroup =>
  Object.values(ValidPermissionsValues).includes(permission as any);

export interface User {
  id?: string;
  created_at?: Date;
  type?: UserType;
  display_name?: string;
  email?: string;
  avatar_url?: string;
}

export interface UserWithPermissions extends User {
  permissions: PermissionGroup[];
}

export const parseUsers = (users: any): UserWithPermissions[] =>
  users.map((user: any) => ({
    ...user,
    permissions: user?.permissions?.filter((p: string) => isValidUserPermission(p))
  }));

// Sync with the matching constant in Portal.
export const ValidLanguages = [
  'us',
  'gb',
  'de',
  'nl',
  'se',
  'eu',
  'es',
  'fr',
  'apac-us',
  'au',
  'nz'
] as const;
export const DefaultLanguage = ValidLanguages[0];
export type Languages = (typeof ValidLanguages)[number];
export const isValidLanguage = (lang: string): lang is Languages =>
  ValidLanguages.includes(lang as Languages);

export type UnwrapArray<T> = T extends (infer U)[] ? U : T;

export type OneOrArray<T, ReqArrayItems extends boolean = true> =
  | T
  | (ReqArrayItems extends true ? MinItemArray<T> : T[]);

export type KeyOfUnion<T, Fallback = never> = T extends T ? keyof T : Fallback;

/** `Object.entries(obj)` shortcut that maintains typing of objects. */
export const typedObjectEntries = <T extends Record<string, any>>(obj: T) =>
  Object.entries(obj) as [keyof T extends string ? keyof T : string, T[KeyOfUnion<T>]][];

/** `Object.keys(obj)` shortcut that maintains typing of objects. */
export const typedObjectKeys = <T extends Record<string, any>>(obj: T) =>
  Object.keys(obj) as (keyof T extends string ? keyof T : string)[];

/** `Object.values(obj)` shortcut that maintains typing of objects. */
export const typedObjectValues = <T extends Record<string, any>>(obj: T) =>
  Object.values(obj) as T[KeyOfUnion<T>][];

export type AllOrNoneFields<Fields extends Record<string, any>> =
  | {
      [K in keyof Fields]?: never;
    }
  | Fields;

export type CamelCase<T extends string | number | symbol> =
  T extends `${infer P1}_${infer P2}${infer P3}`
    ? `${Lowercase<P1>}${Uppercase<P2>}${CamelCase<P3>}`
    : T extends `${infer P1}-${infer P2}${infer P3}`
    ? `${Lowercase<P1>}${Uppercase<P2>}${CamelCase<P3>}`
    : T;

export type KeysToCamelCase<T extends Record<string, any>> = {
  [K in keyof T as CamelCase<K>]: T[K];
};

export enum PageAuditAction {
  Create = 'create',
  Update = 'update',
  RoleAdd = 'role_add',
  RoleRemove = 'role_remove',
  StatusChange = 'status_change',
  CustomContentUpdate = 'custom_content_update',
  ReviewOpen = 'review_open',
  ReviewApprove = 'review_approve',
  ReviewClose = 'review_close',
  ReviewerAdd = 'reviewer_add',
  ReviewerRemove = 'reviewer_remove',
  SetCustomUrl = 'set_custom_url'
}

export interface PageAuditLog {
  id: number;
  page_id: number;
  user_id: string | null;
  type: PageAuditAction;
  data: any;
  created_at: Date;
  referenced_user_id: string | null;

  user?: User | null;
  referenced_user?: User | null;
}

interface ReviewBaseFields {
  id: number;
  is_active: boolean;
  created_at: Date;
  last_approval_at: Date | null;
  slack_message_thread_id: string | null;
}

export interface PageReview extends ReviewBaseFields {
  page_id: number;
}

export interface PageReviewUser {
  id: number;
  review_id: number;
  user_id: string;
  has_approved: boolean;
  created_at: Date;
  user: User | null;
}

export enum NavigationTab {
  Dashboard = 'dashboard',
  Pages = 'pages',
  Experiments = 'experiments',
  Offers = 'offers',
  Notifications = 'notifications',
  Frictionless = 'frictionless',
  Resources = 'resources',
  Gifting = 'gifting',
  Assets = 'assets',
  Users = 'users'
}

export enum ExperimentStatus {
  Draft = 'draft',
  Active = 'active',
  Paused = 'paused',
  Ended = 'ended'
}
export const isExperimentStatus = (status: string): status is ExperimentStatus =>
  Object.values(ExperimentStatus).includes(status as any);

export enum ExperimentTestType {
  ABTest = 'a_b',
  MultiArmedBandit = 'multi_armed_bandit'
}
export const isExperimentTestType = (type: string): type is ExperimentTestType =>
  Object.values(ExperimentTestType).includes(type as any);

export interface Experiment {
  id: number;
  status: ExperimentStatus;
  participating_users_percentage: number;
  name: string;
  hypothesis: string;
  control_page_id: number;
  decided_page_id: number | null;
  created_at: Date;
  last_started_at: Date | null;
  last_paused_at: Date | null;
  abandoned_at: Date | null;
  decided_at: Date | null;
  created_by_user_id: string | null;
  statsig_experiment_id: string | null;
  test_type: ExperimentTestType;
  mab_success_event: string | null;
  mab_event_value: string | null;
  mab_exploration_window_seconds: number | null;
  mab_attribution_window_seconds: number | null;
  mab_winning_threshold_percent: number | null;

  controlPage?: PageRepository;
  createdByUser?: User | null;
}

export interface ExperimentVariantPage {
  id: number;
  name: string;
  index: number;
  experiment_id: number;
  page_id: number;
  created_at: Date;
  statsig_group_id: string | null;
  notes: string | null;
}

// TODO(AssetLibrary-api): Asset variant review system.
export interface AssetReview {
  id: number;
  created_at: Date;
  asset_id: number;
  is_active: boolean;
  last_approval_at: Date | null;
}

// TODO(AssetLibrary-api): Asset variant review system.
export interface AssetReviewUser {
  id: number;
  created_at: Date;
  asset_review_id: number;
  user_id: string;
  has_approved: boolean;
  user: User | null;
}
export interface ResourceHubLink {
  id: number;
  created_at: Date;
  resource_name: string;
  resource_url: string;
  resource_description: string | null;
  resource_thumbnail_url?: string | null;
}

export type Currency = 'USD' | 'GBP' | 'EUR' | 'YEN' | 'AUD' | 'NZD';

export const STORE_CODES = [
  'us',
  'eu',
  'uk',
  'jp',
  'dev',
  'qa',
  'eud',
  'ukd',
  'aud',
  'nzd'
] as const;
export type StoreCode = (typeof STORE_CODES)[number];
export const isValidStoreCode = (storeCode: string): storeCode is StoreCode =>
  STORE_CODES.includes(storeCode as StoreCode);

export const getStoreCodeFromLocale = (locale: Languages): StoreCode => {
  switch (locale) {
    case 'de':
    case 'eu':
    case 'nl':
    case 'se':
    case 'es':
    case 'fr':
      return 'eu';

    case 'gb':
      return 'uk';

    case 'us':
    case 'apac-us':
    case 'au':
    case 'nz':
      return 'us';

    default:
      return assertNever(locale, 'us');
  }
};

export type MaybeReadonly<T> = T | Readonly<T>;

export type ExperimentStatigResults = {
  cumulativeExposures: number;
  updatedAt: string | null;
  groupData: {
    pageId: number;
    isControl: boolean;

    exposures: number;
    CVR: number;
    pValue: number;
    deltaBase: number;
    deltaConfidenceInterval: number;
  }[];
};

export interface PageSeoTags {
  id: number;
  page_id: number;
  title: string;
  description: string | null;
  open_graph_image_sanity_ref_id: string | null;
  keywords: string;
  is_no_index_page: boolean;
  url: string | null;
  internal_canonical: string | null;

  can_missing_locales_exist: boolean;
  structured_schema_data: JSON[];
}

export type DeviceTypes = z.infer<typeof deviceTypesSchema>;

export type AssetLibraryComponentType = Awaited<
  ReturnType<(typeof AtlasApiClient)['getComponentTypeById']>
>['data'];

/**
 * Backend is missing typing for props_schema, so we need to fix it here. This
 * can be removed when backend satisfies our typing.
 */
export const fixAssetLibraryComponentTypePropsSchemaType = (
  input: AssetLibraryComponentType & {
    props_schema: Record<string, any>;
  }
): AssetLibraryComponentType & { props_schema: BackendFormSchema } => ({
  ...input,
  props_schema: {
    formFields:
      'formFields' in input.props_schema && Array.isArray(input.props_schema.formFields)
        ? input.props_schema.formFields
        : [],
    formGroups:
      'formGroups' in input.props_schema && Array.isArray(input.props_schema.formGroups)
        ? input.props_schema.formGroups
        : undefined
  } satisfies BackendFormSchema
});

export type AssetLibraryComponentVariant = Awaited<
  ReturnType<(typeof AtlasApiClient)['getComponentVariantById']>
>['data'];
export type AssetLibraryComponentVariantVersion = Awaited<
  ReturnType<(typeof AtlasApiClient)['getComponentVariantVersionById']>
>['data'];
